import {
  CheckCircleOutlineOutlined,
  Close,
  ExpandLess,
  ExpandMore,
} from "@mui/icons-material";
import {
  Box,
  Button,
  type ButtonProps,
  Checkbox,
  FormControlLabel,
  IconButton,
  Typography,
  useTheme,
} from "@mui/material";
import { type FC, type PropsWithChildren, useState } from "react";

import { Tooltip } from "../../../molecules";
import { ValidationTarget } from "..";
import { FormTab, TabCompletionStatus } from "../form-tab";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type UnsafeFormTab = FormTab<any, any, any>;

export type FormNavigationEntry = {
  className?: string;
  label: string;
  fullLabel?: string;
  path: string;
  slot?: React.ReactNode;
};

export type FormNavigationAction =
  | "cancel"
  | "save"
  | "save-and-back"
  | "save-and-close"
  | "save-and-next"
  | "submit"
  | "delete"
  | "confirm-up-to-date";
export type FormNavigationActionConfig = {
  onClick: (() => void) | (() => Promise<void>);
  key: FormNavigationAction;
  label: string;
  visible: boolean;
  overrideValidationTarget?: ValidationTarget;
  isCheckBoxChecked?: boolean;
  addCheckBoxToForm?: boolean;
  isCheckBoxDisabled?: boolean;
  slot?: string;
  position?: number;
} & ButtonProps;

export type FormNavigationWrapperProps = {
  actions: FormNavigationActionConfig[][];
  activeTab?: string;
  inDialog?: boolean;
  navigationSlot?: React.ReactNode;
  onTabSelected: (tabLabel: string) => void;
  tabs: UnsafeFormTab[];
  title?: string;
  /** Rendered next to the close button in the dialog title bar, e.g. a status chip. */
  titleBadge?: React.ReactNode;
  inline?: boolean;
  onClose?: () => void;
  enableCloseButton?: boolean;
  expandAllChildren?: boolean;
  /** Called for each tab (top-level or child); return false to hide it from the sidebar. */
  isTabVisible?: (tab: UnsafeFormTab) => boolean;
};

export const FormNavigationWrapper: FC<
  PropsWithChildren<FormNavigationWrapperProps>
> = ({
  actions,
  activeTab,
  children,
  inDialog,
  navigationSlot,
  onTabSelected,
  tabs,
  title,
  titleBadge,
  inline,
  onClose,
  enableCloseButton,
  expandAllChildren,
  isTabVisible,
}) => {
  const [left, right] = actions;
  const titleHeight = inDialog ? 60 : 0;
  const theme = useTheme();

  return (
    <Box
      sx={{
        height: !inDialog && !inline ? "calc(100vh - 130px)" : "100%",
      }}
    >
      {inDialog && (
        <Box
          sx={{
            height: `${titleHeight}px`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pt: "12px",
            pb: "12px",
            pl: "24px",
            pr: "24px",
            borderBottom: `${theme.palette.secondary.main} 2px solid`,
          }}
        >
          <Typography variant="h5" component="h2">
            {title}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            {titleBadge}
            {enableCloseButton && (
              <Button onClick={onClose}>
                <Close />
              </Button>
            )}
          </Box>
        </Box>
      )}
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          height:
            !inDialog && !inline
              ? "calc(100vh - 130px)"
              : `calc(100% - ${titleHeight}px)`,
          padding: 0,
          margin: 0,
        }}
      >
        {!inline && (
          <Box
            sx={{
              background: theme.palette.secondary.main,
              display: "flex",
              flex: "0 0 340px",
              flexDirection: "column",
              justifyContent: "space-between",
              padding: 5,
              paddingRight: tabs.some(
                (tab) =>
                  tab.completionStatus ||
                  tab.childTabs?.some((child) => child.completionStatus)
              )
                ? 2
                : 5,
            }}
          >
            <Box
              sx={{
                overflow: "auto",
                scrollbarWidth: "thin",
                "&::-webkit-scrollbar": {
                  width: "0.4em",
                },
                "&::-webkit-scrollbar-track": {
                  background: "#f1f1f1",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "#888",
                },
                "&::-webkit-scrollbar-thumb:hover": {
                  background: "#555",
                },
              }}
            >
              {tabs
                .filter((tab) => !isTabVisible || isTabVisible(tab))
                .map((tab) => {
                  return (
                    <FirstLevelTab
                      tab={tab}
                      key={tab.id}
                      activeTab={activeTab}
                      onTabSelected={onTabSelected}
                      expandAllChildren={expandAllChildren}
                      isTabVisible={isTabVisible}
                    />
                  );
                })}
            </Box>
            {navigationSlot && <Box>{navigationSlot}</Box>}
          </Box>
        )}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
          }}
        >
          <Box
            sx={{
              padding: "46px",
              overflowY: "auto",
              flex: "1 1 100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {children}
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              padding: "12px 46px",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                gap: 2,
              }}
            >
              {left.map(
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                ({ key, overrideValidationTarget, visible, ...action }) =>
                  visible && (
                    <Button
                      sx={{
                        padding: "0px 12px",
                        borderRadius: "4px",
                        textTransform: "none",
                        boxShadow: "none",
                        fontSize: "14px",
                        height: "36px",
                        transition: "background-color 0.2s, color 0.2s",
                      }}
                      key={key}
                      {...action}
                    >
                      {action.label}
                    </Button>
                  )
              )}
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                gap: 2,
              }}
            >
              {right.map(
                ({
                  key,
                  // eslint-disable-next-line @typescript-eslint/no-unused-vars
                  overrideValidationTarget,
                  visible,
                  isCheckBoxChecked,
                  isCheckBoxDisabled,
                  ...action
                }) =>
                  visible &&
                  (key === "confirm-up-to-date" ? (
                    <FormControlLabel
                      control={
                        <Checkbox
                          onChange={action.onClick}
                          color="primary"
                          checked={isCheckBoxChecked}
                          disabled={isCheckBoxDisabled}
                        />
                      }
                      label={action.label}
                      labelPlacement="start"
                      key={key}
                    />
                  ) : (
                    <Button
                      sx={{
                        padding: "0px 12px",
                        borderRadius: "4px",
                        textTransform: "none",
                        boxShadow: "none",
                        fontSize: "14px",
                        height: "36px",
                        transition: "background-color 0.2s, color 0.2s",
                      }}
                      key={key}
                      {...action}
                    >
                      {action.label}
                    </Button>
                  ))
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

function TabCompletionIndicator({ status }: { status: TabCompletionStatus }) {
  const theme = useTheme();

  const color =
    status === "complete"
      ? theme.palette.success.main
      : status === "in-progress"
        ? theme.palette.info.main
        : theme.palette.grey[400];

  return <CheckCircleOutlineOutlined sx={{ fontSize: 24, color, ml: 1 }} />;
}

function FirstLevelTab({
  tab,
  activeTab,
  onTabSelected,
  expandAllChildren = false,
  isTabVisible,
}: {
  tab: UnsafeFormTab;
  activeTab?: string;
  onTabSelected: (label: string) => void;
  expandAllChildren?: boolean;
  isTabVisible?: (tab: UnsafeFormTab) => boolean;
}) {
  const [areChildrenExpanded, setChildrenExpanded] = useState(false);
  const theme = useTheme();
  const visibleChildTabs = tab.childTabs.filter(
    (childTab) => !isTabVisible || isTabVisible(childTab)
  );

  const [prevActiveTab, setPrevActiveTab] = useState(activeTab);
  if (prevActiveTab !== activeTab) {
    setPrevActiveTab(activeTab);
    setChildrenExpanded(
      activeTab === tab.id ||
        tab.childTabs.map((tab) => tab.id).includes(activeTab ?? "")
    );
  }

  return (
    <>
      <Box
        onClick={() => onTabSelected(tab.id)}
        sx={{
          cursor: "pointer",
          height: "28px",
          fontSize: "16px",
          fontWeight: activeTab === tab.id ? 600 : 400,
          borderLeft: `${
            activeTab === tab.id
              ? theme.palette.primary.main
              : theme.palette.secondary.dark
          } 2px solid`,
          color: `${
            activeTab === tab.id ? theme.palette.primary.main : "#000000"
          }`,
          padding: "32px 4px 32px 11px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          transition: "border-left-color 0.3s",
        }}
      >
        {tab.label}
        <>
          {tab.hasErrors ? (
            tab.validationIndicatorSlot
          ) : tab.completionStatus ? (
            <TabCompletionIndicator status={tab.completionStatus} />
          ) : null}
          {visibleChildTabs.length > 0 && (
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                setChildrenExpanded(!areChildrenExpanded);
              }}
            >
              {areChildrenExpanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          )}
        </>
      </Box>
      <Box
        sx={{
          maxHeight: expandAllChildren
            ? "500px"
            : areChildrenExpanded
              ? "500px"
              : 0,
          transition: "max-height 1s",
          overflow: "hidden",
        }}
      >
        {visibleChildTabs.map((childTab) => (
          <SecondLevelTab
            tab={childTab}
            key={childTab.id}
            onTabSelected={onTabSelected}
            activeTab={activeTab}
          />
        ))}
      </Box>
    </>
  );
}

function SecondLevelTab({
  tab,
  activeTab,
  onTabSelected,
}: {
  tab: UnsafeFormTab;
  activeTab?: string;

  onTabSelected: (id: string) => void;
}) {
  const theme = useTheme();
  const tabCmp = (
    <Box
      onClick={() => onTabSelected(tab.id)}
      sx={{
        cursor: "pointer",
        height: "10px",
        fontSize: "14px",
        fontWeight: activeTab === tab.id ? 600 : 400,
        borderLeft: `${
          activeTab === tab.id
            ? theme.palette.primary.main
            : theme.palette.secondary.dark
        } 2px solid`,
        color: `${
          activeTab === tab.id ? theme.palette.primary.main : "#000000"
        }`,
        padding: "16px 4px 16px 24px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        transition: "border-left-color 0.3s",
      }}
    >
      {tab.label}
      {tab.hasErrors ? (
        tab.validationIndicatorSlot
      ) : tab.completionStatus ? (
        <TabCompletionIndicator status={tab.completionStatus} />
      ) : null}
    </Box>
  );

  if (tab.fullLabel) {
    return (
      <Tooltip placement="right" title={tab.fullLabel}>
        {tabCmp}
      </Tooltip>
    );
  }

  return tabCmp;
}
